import React, { useState, useEffect } from "react";
import { InfoCircle } from "iconsax-react";
import { useForm, Controller } from "react-hook-form";
import { useUploadFile } from "../../../components/upload_file_card";
import { AZURE_BLOB_CONTAINER_CERTIFICATES } from "../../../../../constants";
import { CertificateSyscodeString, CERTIFICATE_SYSCODE } from "../../../../constants/certificate_syscode";
import { CertificateProficiencySyscodeString, CERTIFICATE_PROFICIENCY_SYSCODE } from "../../../../constants/certificate_proficiency_syscode";
import { TrainingProviderSyscodeString, TRAINING_PROVIDER_SYSCODE } from "../../../../constants/training_provider_syscode.jsx";
import beveatApiResourceOwner from "../../../../api/resource_owner_beveat_api.js";
import { commonConfigs, urls } from "../../../../../config";

const errorIcon = (
  <div className="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none">
    <InfoCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
  </div>
);

const UploadCertificateTemplateModal = ({
  isOpen,
  onClose,
  onUploadSuccess,
}) => {
  const [file, fileCard, uploadFile, resetFile] = useUploadFile({ 
    container: AZURE_BLOB_CONTAINER_CERTIFICATES,
    acceptedFileTypes: "image",
    displayText: "PNG, JPG files only"
  });
  const [isUploading, setIsUploading] = useState(false);

  const {
    register,
    setError,
    clearErrors,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm({
    defaultValues: {
      trainingProvider: "",
      coursework: "",
      proficiencyLevel: "",
      courseCode: ""
    }
  });

  useEffect(() => {
    if (file) {
      clearErrors("file");
    }
  }, [file, clearErrors]);

  const trainingProviders = Object.entries(TrainingProviderSyscodeString)
    .filter(([key, value]) => key !== "0")
    .map(([key, value]) => ({ value: key, label: value }));

  const courseworkOptions = Object.entries(CertificateSyscodeString)
    .filter(([key, value]) => key !== "0")
    .map(([key, value]) => ({ value: key, label: value }));

  const proficiencyOptions = Object.entries(CertificateProficiencySyscodeString)
    .filter(([key, value]) => key !== "0")
    .map(([key, value]) => ({ value: key, label: value }));

  async function createCertificateTemplate(templateData) {
    const createTemplateUrl = urls.resourceOwnerUrl + commonConfigs.apiUrls.createCertificateTemplate();
    
    await beveatApiResourceOwner.post(createTemplateUrl, templateData, {
      headers: commonConfigs.resourceOwnerApiHeader,
    })
    .then((res) => {
      console.log("Template created successfully:", res.data);
      return res.data;
    })
    .catch((err) => {
      console.error("Error creating template:", err);
      throw err;
    });
  }

  const onSubmit = async (data) => {
    setIsUploading(true);

    if (!file) {
      setError("file", {
        type: "manual",
        message: "Certificate file is required"
      });
      setIsUploading(false);
      return;
    }

    try {
      // Create blob path for templates: templates/{proficiency}/filename
      const proficiencyKey = data.proficiencyLevel.value;
      const proficiencyFolder = proficiencyKey === "1" ? "coc" : "coa";    
      const blobPath = `templates/${proficiencyFolder}/${file.name}`;
      
      console.log('Template upload path:', blobPath);
      
      // Upload file
      const templateUrl = await uploadFile(blobPath);
      console.log('Template uploaded:', templateUrl);
      
      if (templateUrl) {
        const templateData = {
          certificate_syscode: parseInt(data.coursework.value),
          certificate_proficiency_syscode: parseInt(data.proficiencyLevel.value),
          certificate_img_url: templateUrl,
          course_code: data.courseCode,
          training_provider_id: parseInt(data.trainingProvider.value)
        };

        console.log('Template data to save:', templateData);

        await createCertificateTemplate(templateData);

        alert("Template uploaded successfully");
        handleClose();
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      }
    } catch (error) { 
      const errorMessage = error || "An error occurred during upload";
      alert(`Error: ${errorMessage}`);     
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    reset();
    clearErrors();
    resetFile();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-3 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Upload Template</h2>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-6 py-3 space-y-3">
            {/* Training Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Training Provider
              </label>
              <div className="relative">
                <Controller
                  name="trainingProvider"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <select
                      {...field}
                      value={field.value?.value || ""}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        const selectedOption = trainingProviders.find(opt => opt.value === selectedValue);
                        field.onChange(selectedOption || "");
                      }}
                      className={
                        !errors.trainingProvider
                          ? "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm"
                          : "w-full px-3 py-2 border border-red-300 text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm rounded-md"
                      }
                    >
                      <option value="">Select a Training Provider</option>
                      {trainingProviders.map(provider => (
                        <option key={provider.value} value={provider.value}>{provider.label}</option>
                      ))}
                    </select>
                  )}
                />
                {errors.trainingProvider?.type === "required" && errorIcon}
              </div>
            </div>

            {/* Coursework */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coursework
              </label>
              <div className="relative">
                <Controller
                  name="coursework"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <select
                      {...field}
                      value={field.value?.value || ""}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        const selectedOption = courseworkOptions.find(opt => opt.value === selectedValue);
                        field.onChange(selectedOption || "");
                      }}
                      className={
                        !errors.coursework
                          ? "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm"
                          : "w-full px-3 py-2 border border-red-300 text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm rounded-md"
                      }
                    >
                      <option value="">Select a Coursework</option>
                      {courseworkOptions.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  )}
                />
                {errors.coursework?.type === "required" && errorIcon}
              </div>
            </div>

            {/* Certificate Proficiency Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certificate Proficiency Level
              </label>
              <div className="relative">
                <Controller
                  name="proficiencyLevel"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <select
                      {...field}
                      value={field.value?.value || ""}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        const selectedOption = proficiencyOptions.find(opt => opt.value === selectedValue);
                        field.onChange(selectedOption || "");
                      }}
                      className={
                        !errors.proficiencyLevel
                          ? "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm"
                          : "w-full px-3 py-2 border border-red-300 text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm rounded-md"
                      }
                    >
                      <option value="">Select a Proficiency Level</option>
                      {proficiencyOptions.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  )}
                />
                {errors.proficiencyLevel?.type === "required" && errorIcon}
              </div>
            </div>

            {/* Course Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Code
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter Course Code (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm"
                  {...register("courseCode")}
                />
              </div>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Certificate Template File (PNG, JPG)
              </label>
              <div className={errors.file ? "border border-red-300 rounded-md" : ""}>
                {fileCard}
              </div>
              {errors.file?.message && (
                <div>
                  <p className="block text-sm text-red-600 mt-1">
                    {errors.file.message}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isUploading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200 disabled:opacity-50 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 transition-colors duration-200 text-sm ${
                !isUploading
                  ? "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadCertificateTemplateModal;